/**
 * Masters → Chart of accounts. Groups and ledgers as one tree.
 * @file src/web/features/admin/components/masters/ChartTab.tsx
 *
 * This was two tabs, Groups and Ledgers. A CA has one chart of accounts and a
 * ledger only means anything under its group, so the split made you hold the
 * hierarchy in your head while reading a flat alphabetical list — "Bank
 * Accounts" between "Bad Debts" and "Bills Payable" tells you nothing about
 * where it sits.
 *
 * UX decisions worth keeping:
 *
 *   Groups start SHUT. 420 ledgers expanded at once is not a chart, it is a
 *   wall. Shut, it is 36 rows you can read top to bottom, each saying how much
 *   is inside.
 *
 *   Search expands to its matches and nothing else, so typing "hdfc" shows you
 *   the ledger AND the group it lives under. A filter that returned a flat
 *   list of matches would throw away the one thing this screen is for.
 *
 *   Parties appear here, because they ARE ledgers under Sundry Debtors and
 *   Creditors and a CA expects to find them there. Clicking one goes to the
 *   Parties tab rather than opening a stripped-down ledger form — their
 *   compliance fields live there and duplicating a subset would be a second
 *   place to edit the same record.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Search, Pencil, Plus, Users2, Loader2, AlertTriangle, ArrowUpRight } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { adminFetch, adminFetchJson } from '../../lib/admin-api';
import { Loading, ErrBox, Pill } from './shared';
import { GroupEditor, LedgerEditor } from './ChartEditors';
import { OpeningAmountCells, inr } from '../OpeningBalanceCell';

interface Group { id: string; name: string; parent: string | null; nature: string | null }
interface Ledger {
  id: string; name: string; parent: string | null; gstin: string | null;
  openingBalance: number | null; source: string; edited: boolean;
  isParty: boolean; hasProfile: boolean;
}

const NATURE_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'brand'> = {
  asset: 'ok', liability: 'warn', income: 'brand', expense: 'danger',
};

export function ChartTab({ entityId, onOpenParties }: {
  entityId: string | null; onOpenParties: () => void;
}) {
  const [groups, setGroups] = useState<Group[] | null>(null);
  const [ledgers, setLedgers] = useState<Ledger[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [editLedger, setEditLedger] = useState<Ledger | null>(null);
  const [creating, setCreating] = useState<null | 'group'>(null);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(() => {
    if (!entityId) { setError('Select an entity in the header.'); return; }
    setError(null);
    adminFetchJson<{ groups: Group[]; ledgers: Ledger[] }>('/api/masters/chart', { query: { entityId } })
      .then((d) => { setGroups(d.groups); setLedgers(d.ledgers); })
      .catch((e: Error) => setError(e.message));
  }, [entityId]);
  useEffect(load, [load]);

  async function seed() {
    setSeeding(true);
    try {
      const res = await adminFetch('/api/books/groups/seed', { method: 'POST', query: { entityId: entityId ?? undefined } });
      const b = await res.json().catch(() => ({}));
      if (!res.ok || !b.ok) throw new Error(b?.message || 'Failed');
      toast.success(b.created ? `Added ${b.created} standard groups` : 'Chart already complete');
      load();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed'); }
    finally { setSeeding(false); }
  }

  const index = useMemo(() => {
    const g = groups ?? [];
    const known = new Set(g.map((x) => x.name));
    const childGroups = new Map<string, Group[]>();
    const childLedgers = new Map<string, Ledger[]>();
    const roots: Group[] = [];

    for (const grp of g) {
      if (grp.parent && known.has(grp.parent)) {
        childGroups.set(grp.parent, [...(childGroups.get(grp.parent) ?? []), grp]);
      } else {
        roots.push(grp);
      }
    }
    // A ledger whose group is not in the chart still has to appear somewhere,
    // or it is money nobody can see.
    const orphanLedgers: Ledger[] = [];
    for (const l of ledgers) {
      if (l.parent && known.has(l.parent)) {
        childLedgers.set(l.parent, [...(childLedgers.get(l.parent) ?? []), l]);
      } else {
        orphanLedgers.push(l);
      }
    }
    return { childGroups, childLedgers, roots, orphanLedgers };
  }, [groups, ledgers]);

  /**
   * Every group's opening balance = the net of the ledgers beneath it, at any
   * depth. A group never holds a balance of its own — it is derived, which is
   * exactly why it is worth showing: this is the subtotal a CA ticks against
   * the trial balance line for that group before moving to the next one.
   */
  const groupTotals = useMemo(() => {
    const totals = new Map<string, number>();
    const { childGroups, childLedgers } = index;
    const visit = (g: Group, seen: Set<string>): number => {
      let sum = 0;
      for (const l of childLedgers.get(g.name) ?? []) sum += l.openingBalance ?? 0;
      for (const c of childGroups.get(g.name) ?? []) {
        if (seen.has(c.id)) continue; // the tree walk guards cycles; so does this
        sum += visit(c, new Set(seen).add(c.id));
      }
      totals.set(g.id, sum);
      return sum;
    };
    for (const r of index.roots) visit(r, new Set([r.id]));
    return totals;
  }, [index]);

  /**
   * Debits and credits across the whole chart. Opening balances are only right
   * when these two agree — an out-of-balance total means a ledger was keyed on
   * the wrong side or missed, and it is far cheaper to catch here than after
   * the first return is built on it.
   */
  const grand = useMemo(() => {
    let dr = 0, cr = 0;
    for (const l of ledgers) {
      const ob = l.openingBalance ?? 0;
      if (ob < 0) dr += Math.abs(ob);
      else if (ob > 0) cr += ob;
    }
    const r2 = (n: number) => Math.round(n * 100) / 100;
    return { dr: r2(dr), cr: r2(cr), diff: r2(dr - cr) };
  }, [ledgers]);

  /** Groups that contain a match, at any depth — so a hit is never buried. */
  const matched = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return null;
    const hit = new Set<string>();
    const { childGroups, childLedgers } = index;

    const visit = (g: Group, seen: Set<string>): boolean => {
      let found = g.name.toLowerCase().includes(term);
      for (const l of childLedgers.get(g.name) ?? []) {
        if (l.name.toLowerCase().includes(term) || (l.gstin ?? '').toLowerCase().includes(term)) found = true;
      }
      for (const c of childGroups.get(g.name) ?? []) {
        if (seen.has(c.id)) continue;
        if (visit(c, new Set(seen).add(c.id))) found = true;
      }
      if (found) hit.add(g.id);
      return found;
    };
    for (const r of index.roots) visit(r, new Set([r.id]));
    return hit;
  }, [q, index]);

  if (error) return <ErrBox msg={error} />;
  if (!groups) return <Loading label="Loading chart of accounts" />;

  const term = q.trim().toLowerCase();
  const isOpen = (g: Group) => (matched ? matched.has(g.id) : open.has(g.id));
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const unclassified = groups.filter((g) => !g.nature).length;
  const rows: React.ReactNode[] = [];

  const ledgerMatches = (l: Ledger) =>
    !term || l.name.toLowerCase().includes(term) || (l.gstin ?? '').toLowerCase().includes(term);

  const walkGroup = (g: Group, depth: number, seen: Set<string>) => {
    // While searching, a group that contains no match is not shown at all —
    // rendering it shut would leave the whole chart on screen and make the
    // search look broken.
    if (matched && !matched.has(g.id)) return;
    const kidGroups = index.childGroups.get(g.name) ?? [];
    const kidLedgers = (index.childLedgers.get(g.name) ?? []).filter(ledgerMatches);
    const total = (index.childLedgers.get(g.name) ?? []).length;
    const shut = !isOpen(g);

    rows.push(
      <tr key={g.id} className={`border-b border-ink-100 ${!g.nature ? 'bg-warn-50/30' : 'bg-ink-50/40'}`}>
        <td className="px-5 py-2.5">
          <span className="flex items-center gap-1.5" style={{ paddingLeft: depth * 20 }}>
            {kidGroups.length + total > 0 ? (
              <button
                onClick={() => toggle(g.id)}
                aria-expanded={!shut}
                aria-label={`${shut ? 'Expand' : 'Collapse'} ${g.name}`}
                className="rounded p-0.5 text-ink-400 transition hover:bg-ink-200 hover:text-ink-700"
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${shut ? '-rotate-90' : ''}`} />
              </button>
            ) : <span className="w-[1.125rem]" aria-hidden />}
            <span className={depth === 0 ? 'font-semibold text-ink-900' : 'font-medium text-ink-800'}>{g.name}</span>
            {total > 0 && <span className="text-[11px] text-ink-400">{total} ledger{total === 1 ? '' : 's'}</span>}
          </span>
        </td>
        <td className="px-5 py-2.5">
          {g.nature
            ? <Pill tone={NATURE_TONE[g.nature] ?? 'neutral'}>{g.nature}</Pill>
            : <span className="inline-flex items-center gap-1 text-xs text-warn-700"><AlertTriangle className="h-3 w-3" />unclassified</span>}
        </td>
        {/* Blank when the group is empty: a subtotal of nothing is noise, and
            these columns are scanned for real figures. */}
        <OpeningAmountCells value={groupTotals.get(g.id) ?? 0} bold />
        {/* No Edit on a group row. A group is a heading with a subtotal — the
            figures under it belong to its ledgers, and that is where they get
            corrected. The cell stays for column alignment. */}
        <td className="px-5 py-2.5" />
      </tr>,
    );

    if (shut) return;
    for (const c of kidGroups) {
      if (seen.has(c.id)) continue;
      walkGroup(c, depth + 1, new Set(seen).add(c.id));
    }
    for (const l of kidLedgers) rows.push(ledgerRow(l, depth + 1));
  };

  const ledgerRow = (l: Ledger, depth: number) => (
    <tr key={l.id} className="border-b border-ink-100 hover:bg-ink-50/50">
      <td className="px-5 py-2">
        <span className="flex items-center gap-2" style={{ paddingLeft: depth * 20 + 26 }}>
          <Link
            to={`/admin/ledgers/${l.id}`}
            title="Open this ledger's statement in Books"
            className="text-ink-700 no-underline hover:text-inkblack hover:underline"
          >
            {l.name}
          </Link>
          {l.isParty && <Pill tone="brand"><Users2 className="h-3 w-3" />party</Pill>}
          {l.source === 'manual' && <Pill tone="neutral">manual</Pill>}
          {l.edited && <Pill tone="warn">edited</Pill>}
          {l.gstin && <span className="font-mono text-[11px] text-ink-400">{l.gstin}</span>}
        </span>
      </td>
      <td className="px-5 py-2 text-xs text-ink-500" />
      {/* Read-only. The chart is a document to be read — a balance is changed in
          the ledger's Edit dialog, where the name, group and Dr/Cr side are all
          in view and one Save covers the lot. */}
      <OpeningAmountCells value={l.openingBalance} padding="px-5 py-2" />
      <td className="px-5 py-2 text-right">
        {l.isParty ? (
          <button onClick={onOpenParties} className="inline-flex items-center gap-1 rounded-full border border-ink-300 bg-paper px-2.5 py-1 text-[11px] font-semibold text-ink-600 transition hover:bg-ink-50">
            Open in Parties
          </button>
        ) : (
          <button onClick={() => setEditLedger(l)} className="inline-flex items-center gap-1 rounded-full border border-ink-300 bg-paper px-2.5 py-1 text-[11px] font-semibold text-ink-600 transition hover:bg-ink-50">
            <Pencil className="h-3 w-3" /> Edit
          </button>
        )}
      </td>
    </tr>
  );

  for (const r of index.roots) walkGroup(r, 0, new Set([r.id]));
  const strayLedgers = index.orphanLedgers.filter(ledgerMatches);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search groups, ledgers or a GSTIN…"
            className="w-full rounded-xl border border-ink-200 bg-paper py-2.5 pl-9 pr-3 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-500">
            {groups.length} group{groups.length === 1 ? '' : 's'} · {ledgers.length} ledger{ledgers.length === 1 ? '' : 's'}
            {unclassified ? ` · ${unclassified} unclassified` : ''}
          </span>
          <button onClick={seed} disabled={seeding} className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-3.5 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50">
            {seeding && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Standard chart
          </button>
          <button onClick={() => setCreating('group')} className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-paper px-3.5 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50">
            <Plus className="h-3.5 w-3.5" /> Group
          </button>
          <Link to="/admin/ledgers" className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-xs font-semibold text-paper no-underline transition hover:bg-inkblack-80">
            Ledgers in Books <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>

      {unclassified > 0 && !term && (
        <div className="flex items-center gap-2 rounded-xl border border-warn-200 bg-warn-50/60 px-4 py-3 text-sm text-warn-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {unclassified} group{unclassified > 1 ? 's have' : ' has'} no nature set — {unclassified > 1 ? 'they drop' : 'it drops'} off the Balance Sheet and P&amp;L until set.
        </div>
      )}

      {groups.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-14 text-center">
          <p className="text-sm text-ink-600">No chart of accounts yet for this entity.</p>
          <button onClick={seed} disabled={seeding} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-paper disabled:opacity-50">
            {seeding && <Loader2 className="h-4 w-4 animate-spin" />} Set up a standard chart
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Group / ledger</th>
                <th className="px-5 py-3 font-medium">Nature</th>
                <th className="px-5 py-3 text-right font-medium">Debit</th>
                <th className="px-5 py-3 text-right font-medium">Credit</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows}
              {strayLedgers.length > 0 && (
                <>
                  <tr className="border-b border-ink-100 bg-warn-50/40">
                    <td colSpan={5} className="px-5 py-2.5 text-xs font-semibold text-warn-700">
                      <AlertTriangle className="mr-1.5 inline h-3 w-3" />
                      {strayLedgers.length} ledger{strayLedgers.length === 1 ? '' : 's'} under a group that is not in this chart
                    </td>
                  </tr>
                  {strayLedgers.map((l) => ledgerRow(l, 0))}
                </>
              )}
              {rows.length === 0 && strayLedgers.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-ink-500">Nothing matches &ldquo;{q}&rdquo;.</td></tr>
              )}
            </tbody>
            {/* The trial-balance check. Opening balances are only complete when
                total debits equal total credits; showing the difference turns a
                silent data problem into a visible one. Hidden while searching,
                where the rows on screen are a subset and a total would lie. */}
            {!term && (grand.dr !== 0 || grand.cr !== 0) && (
              <tfoot>
                <tr className="border-t-2 border-ink-300 bg-ink-50/60 font-semibold text-ink-900">
                  <td className="px-5 py-3">Grand Total</td>
                  <td className="px-5 py-3" />
                  <td className="px-5 py-3 text-right tabular-nums">{inr(grand.dr)}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{inr(grand.cr)}</td>
                  <td className="px-5 py-3" />
                </tr>
                <tr className={grand.diff === 0 ? 'bg-ok-50/60' : 'bg-danger-50/60'}>
                  <td colSpan={5} className={`px-5 py-2.5 text-xs font-medium ${grand.diff === 0 ? 'text-ok-700' : 'text-danger-700'}`}>
                    {grand.diff === 0 ? (
                      <>Debits equal credits — opening balances are in balance.</>
                    ) : (
                      <>
                        <AlertTriangle className="mr-1.5 inline h-3.5 w-3.5" />
                        Out of balance by {inr(grand.diff)} — {grand.diff > 0 ? 'debits exceed credits' : 'credits exceed debits'}. A ledger is on the wrong side or missing.
                      </>
                    )}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {(editGroup || creating === 'group') && (
        <GroupEditor
          group={editGroup} groups={groups} entityId={entityId}
          onClose={() => { setEditGroup(null); setCreating(null); }}
          onSaved={() => { setEditGroup(null); setCreating(null); load(); }}
        />
      )}
      {editLedger && (
        <LedgerEditor
          ledger={editLedger} entityId={entityId}
          onClose={() => { setEditLedger(null); setCreating(null); }}
          onSaved={() => { setEditLedger(null); setCreating(null); load(); }}
        />
      )}
    </div>
  );
}
